import type { CatalogMuscleSlug } from "./muscleCatalog.js";
import type { Exercise } from "./schemas/exercise.js";
import type { TodayDraftRow } from "./todaySessionProposal.js";
import { roundTo } from "./units.js";
import { resolveExerciseMuscleCredits } from "./volumeByMuscle.js";
import { HARD_SET_MUSCLE_CONTRIB_MIN } from "./weeklyTargetActual.js";

// The sets of a «today» draft row before any time budget, and today's gap
// (P0 57d74690, D3 to D5). Split out of todaySessionProposal.ts.

/** Most sets one exercise takes in the draft (Antenore, 26/09). The reason
 *  line still states the whole weekly gap, so the athlete sees that today
 *  closes part of it. */
export const TODAY_MAX_SETS = 4;

/** The most sets one row takes at `intensityPct` (Antenore, 01/10): the
 *  intensity scales this ceiling too, or a wide budget would undo it. */
export const todayMaxSets = (intensityPct: number): number =>
  Math.max(1, Math.round((TODAY_MAX_SETS * intensityPct) / 100));

/** Sets one exercise takes before any time budget, under `maxSets`. */
export const todaySetCap = (setsToClose: number, maxSets: number = TODAY_MAX_SETS): number =>
  Math.max(1, Math.min(maxSets, setsToClose));

const creditsOf = (ex: Exercise | undefined): Map<CatalogMuscleSlug, number> =>
  new Map(ex ? resolveExerciseMuscleCredits(ex, HARD_SET_MUSCLE_CONTRIB_MIN) : []);

/** D3, D4, D5, top to bottom: today's gap of each row is its target scaled by
 *  `intensityPct`, less the hard sets done this week, less the credit the
 *  rows above it give this muscle (alsoCovers, at their sets before any time
 *  budget). setsToClose follows the gap; a row at 0 is covered and takes no
 *  sets. */
export const regapTodayRows = (
  rows: ReadonlyArray<TodayDraftRow>,
  intensityPct: number,
  catalog: ReadonlyMap<string, Exercise>,
): TodayDraftRow[] => {
  const given = new Map<CatalogMuscleSlug, number>();
  const maxSets = todayMaxSets(intensityPct);
  return rows.map((r) => {
    if (r.targetSets === undefined || r.weekSets === undefined) return r;
    const raw = (r.targetSets * intensityPct) / 100 - r.weekSets - (given.get(r.muscle) ?? 0);
    const gap = Math.max(0, roundTo(raw, 2));
    const alternatives = r.alternatives?.map((a) => {
      const credit = creditsOf(catalog.get(a.exerciseId)).get(r.muscle) ?? 1;
      return { ...a, setsToClose: gap > 0 ? Math.ceil(gap / credit) : 0 };
    });
    const pick = alternatives?.find((a) => a.exerciseId === r.exerciseId);
    const cap = gap > 0 && pick ? todaySetCap(pick.setsToClose, maxSets) : 0;
    for (const [m, c] of creditsOf(catalog.get(r.exerciseId))) {
      if (m !== r.muscle) given.set(m, (given.get(m) ?? 0) + c * cap);
    }
    return {
      ...r,
      gap,
      sets: r.target === "mav" ? 0 : cap,
      ...(alternatives ? { alternatives } : {}),
    };
  });
};
